// The Reports screens' HeroUI pieces: head, crumbs, selects, menus, dialogs and the date range.
import { useId, useRef, useState, type ReactNode } from 'react'
import {
  Alert,
  Breadcrumbs,
  Button,
  Checkbox,
  DateField,
  DateRangePicker,
  Description,
  Dropdown,
  EmptyState,
  Label,
  Link,
  ListBox,
  Modal,
  Radio,
  RadioGroup,
  RangeCalendar,
  SearchField,
  Select,
  Separator,
} from '@heroui/react'
import { parseDate, type DateValue } from '@internationalized/date'
import { Icon, type IconName } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'

/** The page's heading, its one line, and its actions on the right. */
export function PageHead({ title, line, actions, extra, crumbs }: { title: string; line?: ReactNode; actions?: ReactNode; extra?: ReactNode; crumbs?: ReactNode }) {
  return (
    <header className="rp-head">
      {crumbs}
      <div className="rp-head__row">
        <div className="rp-head__titles">
          <div className="rp-head__title">
            <h1 title={title}>{title}</h1>
            {extra}
          </div>
          {line && <p className="rp-head__line">{line}</p>}
        </div>
        {actions && <div className="rp-head__acts">{actions}</div>}
      </div>
    </header>
  )
}

/** Reports › this report; the first step goes back through `onBack`. */
export function Crumbs({ name, onBack }: { name?: string; onBack: () => void }) {
  const { say } = useStaffText()
  // A lone step would be read as the current page; it is a way back.
  if (!name)
    return (
      <nav className="rp-crumbs" aria-label={say('pivot.breadcrumb')}>
        <Link onPress={onBack}>{say('entry.app.reports')}</Link>
      </nav>
    )
  return (
    <Breadcrumbs className="rp-crumbs" aria-label={say('pivot.breadcrumb')} onAction={(key) => key === 'reports' && onBack()}>
      <Breadcrumbs.Item id="reports">{say('entry.app.reports')}</Breadcrumbs.Item>
      <Breadcrumbs.Item id="here">{name}</Breadcrumbs.Item>
    </Breadcrumbs>
  )
}

export interface PickOption {
  value: string
  label: string
  note?: string
}

// '' is a real choice here ("All"); React Aria keys need a non-empty id.
const ALL = '__all'
const keyOf = (v: string) => (v === '' ? ALL : v)

/** One choice from a list; `caption` names what it slices, inside the trigger. */
export function Pick({
  label,
  caption,
  value,
  onChange,
  options,
  className,
  isDisabled,
  suffix,
}: {
  label: string
  caption?: string
  value: string
  onChange: (v: string) => void
  options: readonly PickOption[]
  className?: string
  isDisabled?: boolean
  /** Quiet words after the value, inside the trigger. */
  suffix?: string
}) {
  return (
    <Select
      aria-label={label}
      className={'rp-pick' + (className ? ' ' + className : '')}
      value={keyOf(value)}
      isDisabled={isDisabled}
      onChange={(k) => k != null && onChange(String(k) === ALL ? '' : String(k))}
    >
      <Select.Trigger>
        {caption && <span className="rp-pick__cap">{caption}</span>}
        <Select.Value />
        {suffix && <span className="rp-pick__note">{suffix}</span>}
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {options.map((o) => (
            <ListBox.Item key={keyOf(o.value)} id={keyOf(o.value)} textValue={o.label}>
              {o.note ? (
                <span className="rp-pick__opt">
                  <Label>{o.label}</Label>
                  <Description>{o.note}</Description>
                </span>
              ) : (
                o.label
              )}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}

/** Several choices from a list; at least one stays chosen. */
export function PickMany({ label, caption, summary, value, onChange, options }: { label: string; caption?: string; summary: string; value: string[]; onChange: (v: string[]) => void; options: readonly PickOption[] }) {
  return (
    <Select
      aria-label={label}
      className="rp-pick"
      selectionMode="multiple"
      value={value}
      onChange={(keys) => {
        const next = Array.isArray(keys) ? keys.map(String) : []
        if (next.length > 0) onChange(next)
      }}
    >
      <Select.Trigger>
        {caption && <span className="rp-pick__cap">{caption}</span>}
        <Select.Value>{summary}</Select.Value>
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox selectionMode="multiple">
          {options.map((o) => (
            <ListBox.Item key={o.value} id={o.value} textValue={o.label}>
              {o.label}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}

/** A search box. */
export function Find({ label, value, onChange, className, fullWidth }: { label: string; value: string; onChange: (v: string) => void; className?: string; fullWidth?: boolean }) {
  return (
    <SearchField aria-label={label} value={value} onChange={onChange} className={className} fullWidth={fullWidth}>
      <SearchField.Group>
        <SearchField.SearchIcon />
        <SearchField.Input placeholder={label} autoComplete="off" />
        <SearchField.ClearButton />
      </SearchField.Group>
    </SearchField>
  )
}

/** A refusal or a warning, said once above what it is about. */
export function Notice({ children, tone = 'danger', className }: { children: ReactNode; tone?: 'danger' | 'warning'; className?: string }) {
  return (
    <Alert status={tone} className={'rp-notice' + (className ? ' ' + className : '')} role={tone === 'danger' ? 'alert' : undefined}>
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>{children}</Alert.Title>
      </Alert.Content>
    </Alert>
  )
}

/** Nothing to show: an icon tile, a title, one line, and an optional action. */
export function Empty({ icon, title, children, action }: { icon?: IconName; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <EmptyState className="rp-empty">
      {icon && (
        <span className="rp-empty__icon">
          <Icon name={icon} size={20} />
        </span>
      )}
      <span className="rp-empty__title">{title}</span>
      {children && <span className="rp-empty__line">{children}</span>}
      {action}
    </EmptyState>
  )
}

export type MenuEntry = { id: string; label: string; note?: string; icon?: IconName; danger?: boolean; disabled?: boolean; run: () => void } | 'rule'

/** A button that opens a menu of actions. */
export function ActionMenu({
  label,
  text,
  icon,
  items,
  isDisabled,
  variant = 'secondary',
  iconOnly,
  className,
  alignStart,
}: {
  label: string
  text?: string
  icon?: IconName
  items: MenuEntry[]
  isDisabled?: boolean
  variant?: 'secondary' | 'tertiary' | 'ghost' | 'outline'
  iconOnly?: boolean
  className?: string
  /** The menu lines up with the button's left edge rather than its right. */
  alignStart?: boolean
}) {
  const shown = items.filter((m, i) => m !== 'rule' || (i > 0 && i < items.length - 1))
  const menu = (
    <Dropdown.Menu
      aria-label={label}
      disabledKeys={shown.flatMap((m) => (m !== 'rule' && m.disabled ? [m.id] : []))}
      onAction={(key) => {
        const m = shown.find((x) => x !== 'rule' && x.id === key)
        if (m && m !== 'rule') m.run()
      }}
    >
      {shown.map((m, i) =>
        m === 'rule' ? (
          <Separator key={`rule-${i}`} />
        ) : (
          <Dropdown.Item key={m.id} id={m.id} textValue={m.label} variant={m.danger ? 'danger' : 'default'}>
            {m.icon && <Icon name={m.icon} size={16} />}
            {m.note ? (
              <span className="rp-menu__text">
                <Label>{m.label}</Label>
                <Description>{m.note}</Description>
              </span>
            ) : (
              <Label>{m.label}</Label>
            )}
          </Dropdown.Item>
        ),
      )}
    </Dropdown.Menu>
  )
  return (
    <Dropdown>
      <Button size="sm" variant={variant} isIconOnly={iconOnly} isDisabled={isDisabled} aria-label={iconOnly ? label : undefined} className={className}>
        {icon && <Icon name={icon} size={16} />}
        {!iconOnly && (text ?? label)}
        {!iconOnly && text !== undefined && <Icon name="chevron" size={14} />}
      </Button>
      {alignStart ? (
        <Dropdown.Popover placement="bottom start" className="rp-menu">
          {menu}
        </Dropdown.Popover>
      ) : (
        <Dropdown.Popover placement="bottom end" className="rp-menu">
          {menu}
        </Dropdown.Popover>
      )}
    </Dropdown>
  )
}

/**
 * A centred dialog: title, body, footer buttons. With `onSubmit` the body is a form that
 * submits on Enter; with `askBeforeClose`, closing after an edit asks first.
 */
export function ReportModal({
  title,
  onClose,
  onSubmit,
  footer,
  extra,
  size = 'md',
  askBeforeClose = false,
  children,
}: {
  title: string
  onClose: () => void
  onSubmit?: () => void
  /** Right side of the footer: Cancel, then the main action (type="submit" with `form`). */
  footer?: (formId: string) => ReactNode
  /** Left side of the footer: a destructive or secondary action. */
  extra?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  askBeforeClose?: boolean
  children: ReactNode
}) {
  const { say } = useStaffText()
  const formId = useId()
  const dirty = useRef(false)
  const [asking, setAsking] = useState(false)
  const close = () => (askBeforeClose && dirty.current ? setAsking(true) : onClose())
  const body = onSubmit ? (
    <form
      id={formId}
      className="rp-form"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
      onInput={() => {
        dirty.current = true
      }}
      onChange={() => {
        dirty.current = true
      }}
    >
      {children}
    </form>
  ) : (
    children
  )
  return (
    <Modal.Backdrop isOpen onOpenChange={(open) => !open && close()} className="rp-modal">
      <Modal.Container size={size} scroll="inside">
        <Modal.Dialog>
          <Modal.CloseTrigger aria-label={say('ui.close')} />
          <Modal.Header>
            <Modal.Heading>{title}</Modal.Heading>
          </Modal.Header>
          <Modal.Body>{body}</Modal.Body>
          {(footer || extra || asking) && (
            <Modal.Footer className="rp-modal__foot">
              {asking ? (
                <>
                  <strong className="rp-modal__ask">{say('ui.discardAsk')}</strong>
                  <span className="rp-grow" />
                  <Button variant="tertiary" size="sm" autoFocus onPress={() => setAsking(false)}>
                    {say('ui.keepEditing')}
                  </Button>
                  <Button variant="danger" size="sm" onPress={onClose}>
                    {say('ui.discard')}
                  </Button>
                </>
              ) : (
                <>
                  {extra && <span className="rp-modal__extra">{extra}</span>}
                  <span className="rp-grow" />
                  {footer?.(formId)}
                </>
              )}
            </Modal.Footer>
          )}
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}

const asDate = (iso: string): DateValue | null => {
  try {
    return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? parseDate(iso) : null
  } catch {
    return null
  }
}

/** From and to as one field with a calendar; only a whole range is handed on. */
export function RangePick({ label, from, to, max, isInvalid, onChange }: { label: string; from: string; to: string; max?: string; isInvalid?: boolean; onChange: (from: string, to: string) => void }) {
  const start = asDate(from)
  const end = asDate(to)
  return (
    <DateRangePicker
      aria-label={label}
      className="rp-range"
      value={start && end ? { start, end } : null}
      maxValue={max ? asDate(max) : undefined}
      isInvalid={isInvalid}
      onChange={(v) => v && onChange(v.start.toString(), v.end.toString())}
    >
      <DateField.Group>
        <DateField.Input slot="start">{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
        <DateRangePicker.RangeSeparator />
        <DateField.Input slot="end">{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
        <DateField.Suffix>
          <DateRangePicker.Trigger>
            <DateRangePicker.TriggerIndicator />
          </DateRangePicker.Trigger>
        </DateField.Suffix>
      </DateField.Group>
      <DateRangePicker.Popover>
        <RangeCalendar aria-label={label}>
          <RangeCalendar.Header>
            <RangeCalendar.Heading />
            <RangeCalendar.NavButton slot="previous" />
            <RangeCalendar.NavButton slot="next" />
          </RangeCalendar.Header>
          <RangeCalendar.Grid>
            <RangeCalendar.GridHeader>{(day) => <RangeCalendar.HeaderCell>{day}</RangeCalendar.HeaderCell>}</RangeCalendar.GridHeader>
            <RangeCalendar.GridBody>{(date) => <RangeCalendar.Cell date={date} />}</RangeCalendar.GridBody>
          </RangeCalendar.Grid>
        </RangeCalendar>
      </DateRangePicker.Popover>
    </DateRangePicker>
  )
}

/** One tick box with its words. */
export function Tick({ isSelected, onChange, isDisabled, children }: { isSelected: boolean; onChange: (on: boolean) => void; isDisabled?: boolean; children: ReactNode }) {
  return (
    <Checkbox className="rp-tick" isSelected={isSelected} isDisabled={isDisabled} onChange={onChange}>
      <Checkbox.Content>
        <Checkbox.Control>
          <Checkbox.Indicator />
        </Checkbox.Control>
        {children}
      </Checkbox.Content>
    </Checkbox>
  )
}

/** One of a few, as radios; `note` is a quiet line under a choice. */
export function Choice<K extends string>({
  label,
  value,
  onChange,
  options,
  horizontal,
}: {
  label: string
  value: K
  onChange: (v: K) => void
  /** `tip`: the option's tooltip. */
  options: readonly { value: K; label: string; tip?: string }[]
  horizontal?: boolean
}) {
  return (
    <RadioGroup className="rp-choice" aria-label={label} orientation={horizontal ? 'horizontal' : 'vertical'} value={value} onChange={(v) => onChange(v as K)}>
      {options.map((o) => (
        <Radio key={o.value} value={o.value}>
          <Radio.Content>
            <Radio.Control>
              <Radio.Indicator />
            </Radio.Control>
            <span title={o.tip}>{o.label}</span>
          </Radio.Content>
        </Radio>
      ))}
    </RadioGroup>
  )
}
